/**
 * Dropdown — Editor
 */

import * as React from 'react';
import { Badge, cn, createEmptyDocument } from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    type CompositeQuestionsSidebarRow,
    generateId,
    type ContentEditProps,
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditSortableQuestionsLayout,
    orderItemsByQuestionsSidebarRows,
    persistQuestionsSidebarRows,
    reconcileQuestionsSidebarRows,
    resolvedQuestionsSidebarRows,
    useLocale,
} from '@ai-content-hub/content-core';
import {
    FITBEdit,
    PageEdit,
    defaultPageData,
    defaultFITBConfig,
    getCompositeQuestionType,
    type FITBElementData,
    type FITBConfig,
    type PageElementData,
} from '@ai-content-hub/content-elements';
import type { BlockContentType } from '@ai-content-hub/content-elements';
import type { QtDropdownConfig, QtDropdownData, QtDropdownItem } from './schema';
import { createQtDropdownItem, defaultQtDropdownData } from './schema';
import { FileText, HelpCircle, Settings } from 'lucide-react';
import map from 'lodash/map';
import merge from 'lodash/merge';

const PAGE_BLOCK_TYPES: BlockContentType[] = [
    'rich-text',
    'image',
    'audio',
    'video',
    'text',
    'office-document',
];

const QT_DROPDOWN_QUESTION_TYPE_ID = 'fitb-dropdown';

function fitbConfigForEditor(parent?: QtDropdownConfig): FITBConfig {
    return {
        ...defaultFITBConfig,
        blankMode: 'dropdown',
        shuffleOptions: parent?.shuffleOptions ?? defaultFITBConfig.shuffleOptions,
        caseSensitive: parent?.caseSensitive ?? defaultFITBConfig.caseSensitive,
    };
}

export type DropdownEditProps = ContentEditProps<QtDropdownData> & {
    config?: QtDropdownConfig;
    className?: string;
};

export const DropdownEdit: React.FC<DropdownEditProps> = ({
    value: data,
    onChange,
    disabled = false,
    errors,
    className,
    configSlot,
    config,
    language,
}) => {
    const { t } = useLocale({ config: config?.i18n, initialLanguage: language });
    const [activeTab, setActiveTab] = React.useState<'content' | 'questions' | 'config'>('content');

    const safeData: QtDropdownData = {
        ...defaultQtDropdownData,
        ...data,
        pageContent: {
            ...defaultPageData,
            ...(data?.pageContent ?? {}),
            sections: data?.pageContent?.sections ?? defaultPageData.sections,
        },
        items: data?.items ?? [],
    };

    const items = safeData.items;
    const contentSectionCount = safeData.pageContent.sections?.length ?? 0;

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);

    // EAA-2083: only the exercise selection follows `items`; the section selection is left
    // alone. Every sidebar-rows change (typing a section title included) rebuilds `items`
    // as a new array, so with no exercises yet this effect cleared the selected section on
    // the first keystroke and closed its panel. A section that is really removed is
    // deselected by CompositeEditSortableQuestionsLayout itself.
    React.useEffect(() => {
        if (items.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            if (prev && items.some((i) => i.id === prev)) return prev;
            return items[0].id;
        });
    }, [items]);

    const updateData = (updates: Partial<QtDropdownData>) => {
        onChange({
            ...safeData,
            ...updates,
        });
    };

    const handlePageContentChange = (pageContent: PageElementData) => {
        updateData({ pageContent });
    };

    const addItem = () => {
        if (disabled) return;
        const newItem = createQtDropdownItem(generateId());
        const nextItems = [...items, newItem];
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextItems, (i) => i.id))
            ),
        });
        setSelectedId(newItem.id);
        setSelectedSectionId(null);
        setActiveTab('questions');
    };

    const removeItem = (itemId: string) => {
        if (disabled) return;
        const nextItems = items.filter((i) => i.id !== itemId);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextItems, (i) => i.id))
            ),
        });
    };

    const updateItem = (itemId: string, next: QtDropdownItem) => {
        if (disabled) return;
        updateData({
            items: map(items, (it) => (it.id === itemId ? next : it)),
        });
    };

    const handleQuestionsSidebarRowsChange = (rows: CompositeQuestionsSidebarRow[]) => {
        if (disabled) return;
        const nextItems = orderItemsByQuestionsSidebarRows(items, rows);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(rows),
        });
    };

    const handleAddSidebarSection = () => {
        if (disabled) return;
        const sid = generateId();
        const base = resolvedQuestionsSidebarRows(
            safeData.questionsSidebarRows,
            map(items, (i) => i.id)
        );
        handleQuestionsSidebarRowsChange([
            {
                kind: 'section',
                section: { id: sid, title: '', description: createEmptyDocument() },
            },
            ...base,
        ]);
        setSelectedSectionId(sid);
        setSelectedId(null);
    };

    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(safeData.questionsSidebarRows, map(items, (i) => i.id)),
        [items, safeData.questionsSidebarRows]
    );

    const fitbEditorConfig = fitbConfigForEditor(config);
    const fitbDef = getCompositeQuestionType(QT_DROPDOWN_QUESTION_TYPE_ID);
    const mergedFitbConfig = merge(
        {},
        fitbDef?.defaultConfig ?? {},
        fitbEditorConfig,
    ) as FITBConfig;
    const FitbEditor = (fitbDef?.EditComponent as
        | React.ComponentType<Record<string, unknown>>
        | undefined) ?? FITBEdit;

    const tabs: EditTabDescriptor[] = [
        { id: 'content', label: t('contentTab'), icon: FileText, badge: contentSectionCount },
        { id: 'questions', label: t('questionsTab'), icon: HelpCircle, badge: items.length },
        ...(configSlot ? [{ id: 'config', label: t('configTab'), icon: Settings }] : []),
    ];

    const addExerciseOptions = [
        {
            id: QT_DROPDOWN_QUESTION_TYPE_ID,
            label: fitbDef?.label ?? 'Dropdown',
        },
    ];

    return (
        <CompositeEditShell
            prefix="qt-dropdown-edit"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'content' | 'questions' | 'config')}
            configSlot={configSlot}
            className={cn('qt-dropdown-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    return (
                        <CompositeEditContentTab
                            prefix="qt-dropdown-edit"
                            disabled={disabled}
                            title={{
                                id: 'qt-dd-title',
                                label: t('titleLabel', 'Title'),
                                value: safeData.title ?? '',
                                onChange: (v) => updateData({ title: v }),
                                placeholder: t('titlePlaceholder', 'Exercise title'),
                                required: true,
                                // EAA-2084: show the validator's title error under the input,
                                // in the editor's language rather than the validator's English.
                                error: errors?.title
                                    ? t('titleRequired', 'Please enter a title')
                                    : undefined,
                            }}
                            titleInputClassName="qt-dropdown-edit-title-input"
                            description={{
                                id: 'qt-dd-instructions',
                                label: t('instructionsLabel', 'Instructions (optional)'),
                                value: safeData.instructions ?? '',
                                onChange: (v) => updateData({ instructions: v }),
                                placeholder: t('instructionsPlaceholder', 'Learner-facing instructions'),
                                rows: 3,
                            }}
                            descriptionInputClassName="qt-dropdown-edit-instructions-input"
                            page={
                                <PageEdit
                                    value={safeData.pageContent}
                                    onChange={handlePageContentChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={PAGE_BLOCK_TYPES}
                                    className="qt-dropdown-edit-page-inner"
                                />
                            }
                        />
                    );
                }
                if (tabId === 'questions') {
                    return (
                        <CompositeEditSortableQuestionsLayout
                            prefix="qt-dropdown-edit"
                            listTitle={t('exercisesListTitle', 'Exercises')}
                            emptyMessage={t('emptyExercisesMsg', 'No exercises yet. Add a dropdown passage.')}
                            items={items}
                            selectedId={selectedId}
                            onSelectId={setSelectedId}
                            selectedSectionId={selectedSectionId}
                            onSelectSectionId={setSelectedSectionId}
                            disabled={disabled}
                            minItemsToKeep={0}
                            sidebarRows={sidebarRows}
                            onSidebarRowsChange={handleQuestionsSidebarRowsChange}
                            onAddSidebarSection={handleAddSidebarSection}
                            addSidebarSectionLabel={t('addSection', 'Add section')}
                            removeSidebarSectionLabel={t('removeSection', 'Remove section')}
                            onRemoveItem={removeItem}
                            getItemLabel={(_itemId, index) =>
                                `${index + 1}. ${t('dropdownBlanksPreview', 'Dropdown blanks')}`
                            }
                            addButtonLabel={t('addExercise', 'Add exercise')}
                            addButtonOptions={addExerciseOptions}
                            addButtonClassName="qt-dropdown-edit-add"
                            onAddItem={() => addItem()}
                            sectionDetailTitle={t('sectionDetailTitle', 'Section')}
                            detailTitle={t('exerciseDetailTitle', 'Exercise')}
                            detailBadge={
                                <Badge
                                    variant="outline"
                                    className="border-success/30 bg-success/10"
                                >
                                    {fitbDef?.label ?? t('dropdownBadge', 'Dropdown')}
                                </Badge>
                            }
                            renderItemDetail={({ itemId }) => {
                                const rowItem = items.find((i) => i.id === itemId);
                                if (!rowItem) {
                                    return null;
                                }
                                return (
                                    <FitbEditor
                                        value={rowItem.data}
                                        onChange={(d: unknown) =>
                                            updateItem(rowItem.id, {
                                                ...rowItem,
                                                data: d as FITBElementData,
                                            })
                                        }
                                        config={mergedFitbConfig}
                                        disabled={disabled}
                                    />
                                );
                            }}
                            detailContent={null}
                            detailEmptyMessage={t('detailEmptyMessage', 'Select an exercise, a section, or add one to start editing.')}
                            removeItemLabel={t('removeExercise', 'Remove exercise')}
                            richTextTranslate={t}
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default DropdownEdit;
